import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import { showSnackbar } from "../../redux/snackbar/actions";
import { setAuth } from "../../redux/common/actions";
import {
  setEncryptedCookie,
  getDecryptedCookie,
  removeCookie,
  encryptAES,
  decryptAES,
} from "../../utils";
import Icon from "../../assets/img/icon.png";
import Banner from "../../assets/img/tagitstore_banner-01.jpg";
import authApi from "../../services/auth";
import userApi from "../../services/user";

const useStyles = makeStyles((theme) => ({
  btn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bannerImage: {
    backgroundSize: "cover",
    backgroundPosition: "right",
    [theme.breakpoints.up("md")]: {
      backgroundImage: `url(${Banner})`,
      backgroundPosition: "center",
      backgroundSize: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  bg: {
    backgroundColor: "#f7f4f2",
  },
}));

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="" sx={{ textDecoration: "none" }}>
        TAGITStore
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const SignIn = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [shouldFetchMenu, setShouldFetchMenu] = useState(false);

  useEffect(() => {
    const userAuth = getDecryptedCookie("uuid");
    if (userAuth) {
      navigate("/home");
    }
  }, [navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  let lastSubmitTime = 0;
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    const handleUserInteraction = () => setUserInteracted(true);

    // document.addEventListener("mousemove", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("scroll", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      // document.removeEventListener("mousemove", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("scroll", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);

  const isBotDetected = () => {
    // Skip bot detection for mobile devices
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return false;
    }

    // Check for common bot indicators only on desktop
    return (
        (navigator.webdriver && !/Android|iPhone/i.test(navigator.userAgent)) ||
        (!navigator.languages || navigator.languages.length === 0) ||
        (/bot|crawler|spider|headless|phantom/i.test(navigator.userAgent) && 
         !/Chrome|Firefox|Safari/i.test(navigator.userAgent)) ||
        /HeadlessChrome|puppeteer|phantomjs/i.test(navigator.userAgent) ||
        (!userInteracted && !/Android|iPhone/i.test(navigator.userAgent))
    );
};

  const handleSubmit = (formData) => {
    const now = Date.now();

    if (isBotDetected()) {
      alert("Bot access is restricted!");
      window.location.reload();
      return;
    }

    if (now - lastSubmitTime < 3000) {
      alert("Multiple rapid submissions detected!");
      window.location.reload();
      return;
    }

    lastSubmitTime = now;

    if (formData?.honeypot) {
      alert("Suspicious activity detected!");
      return;
    }
    const data = {
      email: formData?.email?.toLowerCase(),
      password: formData?.password,
    };
    const encryptedData = encryptAES(data);
    removeCookie("email");
    removeCookie("password");
    removeCookie("id");
    removeCookie("abc");
    removeCookie("uuid");
    removeCookie("rt");
    removeCookie("at");
    dispatch(showLoader());

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        const data = decryptAES(
          res?.data?.encryptedDataRes?.encryptedData,
          res?.data?.encryptedDataRes?.iv
        );
        const userId = data?.uuid;

        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        dispatch(setAuth({ isAuthenticated: true }));
        setEncryptedCookie("uuid", userId, { expires: 7 });

        formik.resetForm();
        navigate("/home");
        editUserById(userId).then(() => {
          setShouldFetchMenu(true);
        });
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Invalid Credentials",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );

      removeCookie("email");
      removeCookie("password");
    };

    authApi.login(encryptedData).then(onSuccess).catch(onFailure);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      formik.handleSubmit();
    }
  };

  const editUserById = async (userId) => {
    dispatch(showLoader());
    try {
      const response = await userApi.getUserId(userId);
      if (response?.data?.status) {
        setEncryptedCookie("abc", response.data.data[0].role.uuid);
        setShouldFetchMenu(true);
        const id = response.data.data[0].role.uuid;
        setEncryptedCookie("id", id, { expires: 7 });
      } else {
        console.error("Error fetching user data");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email"
        )
        .required("Email required"),
      password: yup
        .string()
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
          "Password required"
        )
        .required("Password Required"),
    }),
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const savedEmail = getDecryptedCookie("email");
    const savedPassword = getDecryptedCookie("password");

    if (savedEmail) {
      formik.setFieldValue("email", savedEmail);
    }
    if (savedPassword) {
      formik.setFieldValue("password", savedPassword);
    }
  }, []);

  return (
    <>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid item xs={false} md={8} className={classes.bannerImage} />
        <Grid
          item
          xs={12}
          md={4}
          pt={5}
          component={Paper}
          elevation={6}
          square
          className={classes.bg}
        >
          <Box
            sx={{
              my: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={Icon}
              alt="logo"
              sx={{ my: 2, width: "75px", height: "auto", maxWidth: "100%" }}
            />
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            <Box sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="off"
                value={formik.values.email}
                onChange={formik.handleChange}
                onKeyDown={handleKeyDown}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Password"
                name="password"
                disabled={!formik.values.email}
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onKeyDown={handleKeyDown}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={formik.handleSubmit}
                fullWidth
                sx={{
                  mt: 3,
                  background:
                    "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                  color: "#fff",
                  borderRadius: "8px",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                  },
                }}
              >
                Sign In
              </Button>
              <Grid container mt={1}>
                <Grid item xs>
                  <Link
                    href="/forgotpassword"
                    variant="body2"
                    sx={{ textDecoration: "none", color: "#6c6c6c" }}
                  >
                    Forgot password?
                  </Link>
                </Grid>
              </Grid>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SignIn;
